/**
 * Post-Launch Monitoring Service
 * Comprehensive monitoring for production deployment including error rates,
 * performance tracking, user adoption metrics, and optimization planning
 */

import { performanceService } from './performanceService';
import { isProduction } from '../config/production';

export interface PostLaunchMetrics {
  // Error Monitoring
  errorRate: number;
  criticalErrors: number;
  errorTrends: ErrorTrend[];
  
  // Performance Monitoring
  performanceScore: number;
  coreWebVitals: {
    lcp: number | null;
    inp: number | null;
    cls: number | null;
    fcp: number | null;
    ttfb: number | null;
  };
  performanceTrends: PerformanceTrend[];
  
  // User Adoption
  totalUsers: number;
  activeUsers: number;
  newUsers: number;
  returningUsers: number;
  userGrowthRate: number;
  
  // Engagement Metrics
  sessionDuration: number;
  pageViewsPerSession: number;
  bounceRate: number;
  featureUsage: FeatureUsageMetric[];
  
  // PWA Metrics
  installationRate: number;
  pwaInstalls: number;
  offlineUsage: number;
  
  // Business Metrics
  conversionRate: number;
  readingsCreated: number;
  shopPurchases: number;
  
  // Feedback & Issues
  userFeedback: UserFeedbackSummary[];
  issueReports: number;
  
  timestamp: string;
}

export interface ErrorTrend {
  date: string;
  errorCount: number;
  criticalCount: number;
  errorRate: number;
}

export interface PerformanceTrend {
  date: string;
  performanceScore: number;
  lcp: number;
  inp: number;
  cls: number;
}

export interface FeatureUsageMetric {
  feature: string;
  usage: number;
  trend: 'up' | 'down' | 'stable';
  adoptionRate: number;
}

export interface UserFeedbackSummary {
  type: 'bug' | 'feature' | 'improvement' | 'other';
  count: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  topIssues: string[];
}

export interface OptimizationRecommendation {
  category: 'performance' | 'user_experience' | 'feature' | 'technical';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  impact: string;
  effort: 'low' | 'medium' | 'high';
  metrics: string[];
}

export interface MonitoringAlert {
  id: string;
  type: 'error_spike' | 'performance_degradation' | 'user_drop' | 'conversion_drop';
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  timestamp: string;
  resolved: boolean;
  metrics: Record<string, number>;
}

class PostLaunchMonitoringService {
  private isInitialized = false;
  private metricsHistory: PostLaunchMetrics[] = [];
  private alerts: MonitoringAlert[] = [];
  private alertThresholds = {
    errorRateSpike: 0.05, // 5% error rate
    performanceDrop: 20, // 20 point drop in performance score
    userDropoff: 0.3, // 30% drop in active users
    conversionDrop: 0.2, // 20% drop in conversion rate
  };

  /**
   * Initialize post-launch monitoring
   */
  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    this.startPeriodicMonitoring();
    this.setupAlertSystem();
    this.loadHistoricalData();
    
    this.isInitialized = true;
    console.log('Post-launch monitoring service initialized');
  }

  /**
   * Get current post-launch metrics
   */
  public async getCurrentMetrics(): Promise<PostLaunchMetrics> {
    const [
      errorMetrics,
      performanceMetrics,
      userMetrics,
      engagementMetrics,
      pwaMetrics,
      businessMetrics,
      feedbackMetrics
    ] = await Promise.all([
      this.getErrorMetrics(),
      this.getPerformanceMetrics(),
      this.getUserAdoptionMetrics(),
      this.getEngagementMetrics(),
      this.getPWAMetrics(),
      this.getBusinessMetrics(),
      this.getFeedbackMetrics()
    ]);

    const metrics: PostLaunchMetrics = {
      // Default values
      errorRate: 0,
      criticalErrors: 0,
      errorTrends: [],
      performanceScore: 0,
      coreWebVitals: { lcp: null, inp: null, cls: null, fcp: null, ttfb: null },
      performanceTrends: [],
      totalUsers: 0,
      activeUsers: 0,
      newUsers: 0,
      returningUsers: 0,
      userGrowthRate: 0,
      sessionDuration: 0,
      pageViewsPerSession: 0,
      bounceRate: 0,
      featureUsage: [],
      installationRate: 0,
      pwaInstalls: 0,
      offlineUsage: 0,
      conversionRate: 0,
      readingsCreated: 0,
      shopPurchases: 0,
      userFeedback: [],
      issueReports: 0,
      timestamp: new Date().toISOString(),
      // Override with actual metrics
      ...errorMetrics,
      ...performanceMetrics,
      ...userMetrics,
      ...engagementMetrics,
      ...pwaMetrics,
      ...businessMetrics,
      ...feedbackMetrics,
    };

    // Store metrics for trend analysis
    this.metricsHistory.push(metrics);
    this.checkForAlerts(metrics);

    return metrics;
  }

  /**
   * Get optimization recommendations based on current metrics
   */
  public getOptimizationRecommendations(metrics: PostLaunchMetrics): OptimizationRecommendation[] {
    const recommendations: OptimizationRecommendation[] = [];

    // Performance recommendations
    if (metrics.performanceScore < 80) {
      recommendations.push({
        category: 'performance',
        priority: 'high',
        title: 'Improve Core Web Vitals',
        description: 'Performance score is below optimal threshold. Focus on LCP, INP, and CLS improvements.',
        impact: 'Better user experience and SEO rankings',
        effort: 'medium',
        metrics: ['performanceScore', 'lcp', 'inp', 'cls']
      });
    }

    // Error rate recommendations
    if (metrics.errorRate > 0.02) {
      recommendations.push({
        category: 'technical',
        priority: 'high',
        title: 'Reduce Error Rate',
        description: 'Error rate is above 2%. Investigate and fix common error patterns.',
        impact: 'Improved user experience and reliability',
        effort: 'medium',
        metrics: ['errorRate', 'criticalErrors']
      });
    }

    // User engagement recommendations
    if (metrics.bounceRate > 0.6) {
      recommendations.push({
        category: 'user_experience',
        priority: 'medium',
        title: 'Improve User Engagement',
        description: 'High bounce rate indicates users are leaving quickly. Review onboarding and initial experience.',
        impact: 'Higher user retention and engagement',
        effort: 'high',
        metrics: ['bounceRate', 'sessionDuration']
      });
    }

    // PWA adoption recommendations
    if (metrics.installationRate < 0.1) {
      recommendations.push({
        category: 'feature',
        priority: 'medium',
        title: 'Increase PWA Installation Rate',
        description: 'Low PWA installation rate. Improve install prompts and highlight PWA benefits.',
        impact: 'Better user retention and engagement',
        effort: 'low',
        metrics: ['installationRate', 'pwaInstalls']
      });
    }

    // Conversion recommendations
    if (metrics.conversionRate < 0.05) {
      recommendations.push({
        category: 'user_experience',
        priority: 'high',
        title: 'Optimize Conversion Funnel',
        description: 'Low conversion rate. Analyze user journey and remove friction points.',
        impact: 'Increased revenue and user value',
        effort: 'high',
        metrics: ['conversionRate', 'readingsCreated', 'shopPurchases']
      });
    }

    // Feature usage recommendations
    const lowUsageFeatures = metrics.featureUsage.filter(f => f.adoptionRate < 0.2);
    if (lowUsageFeatures.length > 0) {
      recommendations.push({
        category: 'feature',
        priority: 'low',
        title: 'Improve Feature Discovery',
        description: `Low adoption for features: ${lowUsageFeatures.map(f => f.feature).join(', ')}. Consider better onboarding or UI improvements.`,
        impact: 'Better feature utilization and user value',
        effort: 'medium',
        metrics: ['featureUsage']
      });
    }

    return recommendations.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  /**
   * Get active monitoring alerts
   */
  public getActiveAlerts(): MonitoringAlert[] {
    return this.alerts.filter(alert => !alert.resolved);
  }

  /**
   * Resolve a monitoring alert
   */
  public resolveAlert(alertId: string): void {
    const alert = this.alerts.find(a => a.id === alertId);
    if (alert) {
      alert.resolved = true;
    }
  }

  /**
   * Get metrics history for trend analysis
   */
  public getMetricsHistory(days: number = 7): PostLaunchMetrics[] {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return this.metricsHistory.filter(metrics => 
      new Date(metrics.timestamp) >= cutoffDate
    );
  }

  /**
   * Export monitoring data for analysis
   */
  public exportMonitoringData(): string {
    return JSON.stringify({
      currentMetrics: this.metricsHistory[this.metricsHistory.length - 1],
      metricsHistory: this.metricsHistory,
      alerts: this.alerts,
      recommendations: this.getOptimizationRecommendations(
        this.metricsHistory[this.metricsHistory.length - 1] || {} as PostLaunchMetrics
      ),
      exportTimestamp: new Date().toISOString()
    }, null, 2);
  }

  /**
   * Get error metrics
   */
  private async getErrorMetrics(): Promise<Partial<PostLaunchMetrics>> {
    const errors = this.getStoredData('errorTracking_errors', []);
    const totalEvents = this.getStoredData('analytics_events', []).length;
    
    const criticalErrors = errors.filter((error: any) => 
      /security|authentication|payment|data loss|corruption/i.test(error.message)
    ).length;
    
    const errorRate = totalEvents > 0 ? errors.length / totalEvents : 0;
    
    // Calculate error trends (last 7 days)
    const errorTrends = this.calculateErrorTrends(errors);

    return {
      errorRate,
      criticalErrors,
      errorTrends
    };
  }

  /**
   * Get performance metrics
   */
  private async getPerformanceMetrics(): Promise<Partial<PostLaunchMetrics>> {
    const currentMetrics = performanceService.getMetrics();
    const performanceScore = performanceService.getPerformanceScore();
    
    // Calculate performance trends
    const performanceTrends = this.calculatePerformanceTrends();

    return {
      performanceScore,
      coreWebVitals: {
        lcp: currentMetrics.lcp,
        inp: currentMetrics.inp,
        cls: currentMetrics.cls,
        fcp: currentMetrics.fcp,
        ttfb: currentMetrics.ttfb
      },
      performanceTrends
    };
  }

  /**
   * Get user adoption metrics
   */
  private async getUserAdoptionMetrics(): Promise<Partial<PostLaunchMetrics>> {
    const events = this.getStoredData('analytics_events', []);
    const users = this.getUniqueValues(events, 'userId').filter(Boolean);
    
    // Calculate user metrics
    const totalUsers = users.length;
    const activeUsers = this.getActiveUsers(events);
    const { newUsers, returningUsers } = this.getUserTypes(events);
    const userGrowthRate = this.calculateUserGrowthRate(events);

    return {
      totalUsers,
      activeUsers,
      newUsers,
      returningUsers,
      userGrowthRate
    };
  }

  /**
   * Get engagement metrics
   */
  private async getEngagementMetrics(): Promise<Partial<PostLaunchMetrics>> {
    const events = this.getStoredData('analytics_events', []);
    const pageViews = this.getStoredData('analytics_pageviews', []);
    
    const sessionDuration = this.calculateAverageSessionDuration(events);
    const pageViewsPerSession = this.calculatePageViewsPerSession(pageViews, events);
    const bounceRate = this.calculateBounceRate(events);
    const featureUsage = this.calculateFeatureUsage(events);

    return {
      sessionDuration,
      pageViewsPerSession,
      bounceRate,
      featureUsage
    };
  }

  /**
   * Get PWA metrics
   */
  private async getPWAMetrics(): Promise<Partial<PostLaunchMetrics>> {
    const events = this.getStoredData('analytics_events', []);
    
    const pwaEvents = events.filter((event: any) => 
      event.name.includes('pwa_') || event.category === 'engagement'
    );
    
    const installPrompts = pwaEvents.filter((event: any) => 
      event.name === 'pwa_install_prompt_shown'
    ).length;
    
    const pwaInstalls = pwaEvents.filter((event: any) => 
      event.name === 'pwa_installed'
    ).length;
    
    const offlineEvents = pwaEvents.filter((event: any) => 
      event.name === 'offline_usage'
    ).length;
    
    const installationRate = installPrompts > 0 ? pwaInstalls / installPrompts : 0;

    return {
      installationRate,
      pwaInstalls,
      offlineUsage: offlineEvents
    };
  }

  /**
   * Get business metrics
   */
  private async getBusinessMetrics(): Promise<Partial<PostLaunchMetrics>> {
    const events = this.getStoredData('analytics_events', []);
    
    const readingEvents = events.filter((event: any) => 
      event.name === 'feature_created' && event.properties?.featureName === 'tarot_reading'
    );
    
    const purchaseEvents = events.filter((event: any) => 
      event.name === 'feature_purchase' && event.properties?.featureName === 'shop'
    );
    
    const conversionEvents = events.filter((event: any) => 
      event.name.startsWith('conversion_')
    );
    
    const totalSessions = this.getUniqueValues(events, 'sessionId').length;
    const conversionRate = totalSessions > 0 ? conversionEvents.length / totalSessions : 0;

    return {
      conversionRate,
      readingsCreated: readingEvents.length,
      shopPurchases: purchaseEvents.length
    };
  }

  /**
   * Get feedback metrics
   */
  private async getFeedbackMetrics(): Promise<Partial<PostLaunchMetrics>> {
    const feedback = this.getStoredData('errorTracking_feedback', []);
    
    const userFeedback: UserFeedbackSummary[] = [
      'bug', 'feature', 'improvement', 'other'
    ].map(type => {
      const typeFeedback = feedback.filter((f: any) => f.type === type);
      return {
        type: type as any,
        count: typeFeedback.length,
        sentiment: this.analyzeSentiment(typeFeedback),
        topIssues: this.extractTopIssues(typeFeedback)
      };
    });
    
    const issueReports = feedback.filter((f: any) => f.type === 'bug').length;

    return {
      userFeedback,
      issueReports
    };
  }

  /**
   * Calculate error trends
   */
  private calculateErrorTrends(errors: any[]): ErrorTrend[] {
    const trends: ErrorTrend[] = [];
    const days = 7;
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayErrors = errors.filter((error: any) => 
        error.timestamp.startsWith(dateStr)
      );
      
      const criticalCount = dayErrors.filter((error: any) => 
        /security|authentication|payment|data loss|corruption/i.test(error.message)
      ).length;
      
      trends.push({
        date: dateStr,
        errorCount: dayErrors.length,
        criticalCount,
        errorRate: dayErrors.length / Math.max(1, errors.length)
      });
    }
    
    return trends;
  }

  /**
   * Calculate performance trends
   */
  private calculatePerformanceTrends(): PerformanceTrend[] {
    // In a real implementation, this would use historical performance data
    // For now, we'll simulate trends based on current metrics
    const currentMetrics = performanceService.getMetrics();
    const trends: PerformanceTrend[] = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      trends.push({
        date: date.toISOString().split('T')[0],
        performanceScore: performanceService.getPerformanceScore() + (Math.random() - 0.5) * 10,
        lcp: (currentMetrics.lcp || 2000) + (Math.random() - 0.5) * 500,
        inp: (currentMetrics.inp || 100) + (Math.random() - 0.5) * 50,
        cls: (currentMetrics.cls || 0.1) + (Math.random() - 0.5) * 0.05
      });
    }
    
    return trends;
  }

  /**
   * Get unique values from array of objects
   */
  private getUniqueValues(array: any[], key: string): any[] {
    return [...new Set(array.map(item => item[key]))];
  }

  /**
   * Get active users (users with activity in last 24 hours)
   */
  private getActiveUsers(events: any[]): number {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    const recentEvents = events.filter((event: any) => 
      new Date(event.timestamp) >= yesterday
    );
    
    return this.getUniqueValues(recentEvents, 'userId').filter(Boolean).length;
  }

  /**
   * Get new vs returning users
   */
  private getUserTypes(events: any[]): { newUsers: number; returningUsers: number } {
    const userFirstSeen = new Map<string, Date>();
    
    events.forEach((event: any) => {
      if (event.userId) {
        const eventDate = new Date(event.timestamp);
        if (!userFirstSeen.has(event.userId) || eventDate < userFirstSeen.get(event.userId)!) {
          userFirstSeen.set(event.userId, eventDate);
        }
      }
    });
    
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    let newUsers = 0;
    let returningUsers = 0;
    
    userFirstSeen.forEach((firstSeen) => {
      if (firstSeen >= yesterday) {
        newUsers++;
      } else {
        returningUsers++;
      }
    });
    
    return { newUsers, returningUsers };
  }

  /**
   * Calculate user growth rate
   */
  private calculateUserGrowthRate(events: any[]): number {
    const thisWeek = events.filter((event: any) => {
      const eventDate = new Date(event.timestamp);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return eventDate >= weekAgo;
    });
    
    const lastWeek = events.filter((event: any) => {
      const eventDate = new Date(event.timestamp);
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return eventDate >= twoWeeksAgo && eventDate < weekAgo;
    });
    
    const thisWeekUsers = this.getUniqueValues(thisWeek, 'userId').filter(Boolean).length;
    const lastWeekUsers = this.getUniqueValues(lastWeek, 'userId').filter(Boolean).length;
    
    return lastWeekUsers > 0 ? (thisWeekUsers - lastWeekUsers) / lastWeekUsers : 0;
  }

  /**
   * Calculate average session duration
   */
  private calculateAverageSessionDuration(events: any[]): number {
    const sessionDurations = new Map<string, { start: Date; end: Date }>();
    
    events.forEach((event: any) => {
      const eventDate = new Date(event.timestamp);
      const sessionId = event.sessionId;
      
      if (!sessionDurations.has(sessionId)) {
        sessionDurations.set(sessionId, { start: eventDate, end: eventDate });
      } else {
        const session = sessionDurations.get(sessionId)!;
        if (eventDate < session.start) session.start = eventDate;
        if (eventDate > session.end) session.end = eventDate;
      }
    });
    
    const durations = Array.from(sessionDurations.values()).map(session => 
      session.end.getTime() - session.start.getTime()
    );
    
    return durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;
  }

  /**
   * Calculate page views per session
   */
  private calculatePageViewsPerSession(pageViews: any[], events: any[]): number {
    const sessions = this.getUniqueValues(events, 'sessionId');
    return sessions.length > 0 ? pageViews.length / sessions.length : 0;
  }

  /**
   * Calculate bounce rate
   */
  private calculateBounceRate(events: any[]): number {
    const sessionEvents = new Map<string, number>();
    
    events.forEach((event: any) => {
      const sessionId = event.sessionId;
      sessionEvents.set(sessionId, (sessionEvents.get(sessionId) || 0) + 1);
    });
    
    const singleEventSessions = Array.from(sessionEvents.values()).filter(count => count === 1).length;
    const totalSessions = sessionEvents.size;
    
    return totalSessions > 0 ? singleEventSessions / totalSessions : 0;
  }

  /**
   * Calculate feature usage metrics
   */
  private calculateFeatureUsage(events: any[]): FeatureUsageMetric[] {
    const featureEvents = events.filter((event: any) => 
      event.category === 'feature_usage'
    );
    
    const featureUsage = new Map<string, number>();
    
    featureEvents.forEach((event: any) => {
      const feature = event.properties?.featureName || event.name;
      featureUsage.set(feature, (featureUsage.get(feature) || 0) + 1);
    });
    
    const totalUsers = this.getUniqueValues(events, 'userId').filter(Boolean).length;
    
    return Array.from(featureUsage.entries()).map(([feature, usage]) => ({
      feature,
      usage,
      trend: 'stable' as const, // Would calculate from historical data
      adoptionRate: totalUsers > 0 ? usage / totalUsers : 0
    }));
  }

  /**
   * Analyze sentiment of feedback
   */
  private analyzeSentiment(feedback: any[]): 'positive' | 'neutral' | 'negative' {
    // Simple sentiment analysis based on keywords
    const positiveKeywords = ['good', 'great', 'excellent', 'love', 'amazing', 'perfect'];
    const negativeKeywords = ['bad', 'terrible', 'awful', 'hate', 'broken', 'bug', 'error'];
    
    let positiveCount = 0;
    let negativeCount = 0;
    
    feedback.forEach((f: any) => {
      const message = f.message.toLowerCase();
      positiveKeywords.forEach(keyword => {
        if (message.includes(keyword)) positiveCount++;
      });
      negativeKeywords.forEach(keyword => {
        if (message.includes(keyword)) negativeCount++;
      });
    });
    
    if (positiveCount > negativeCount) return 'positive';
    if (negativeCount > positiveCount) return 'negative';
    return 'neutral';
  }

  /**
   * Extract top issues from feedback
   */
  private extractTopIssues(feedback: any[]): string[] {
    const issues = new Map<string, number>();
    
    feedback.forEach((f: any) => {
      // Extract key phrases (simplified)
      const words = f.message.toLowerCase().split(/\s+/);
      words.forEach((word: string) => {
        if (word.length > 4) {
          issues.set(word, (issues.get(word) || 0) + 1);
        }
      });
    });
    
    return Array.from(issues.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([issue]) => issue);
  }

  /**
   * Check for alerts based on current metrics
   */
  private checkForAlerts(metrics: PostLaunchMetrics): void {
    const previousMetrics = this.metricsHistory[this.metricsHistory.length - 2];
    
    if (!previousMetrics) return;
    
    // Error rate spike alert
    if (metrics.errorRate > this.alertThresholds.errorRateSpike) {
      this.createAlert('error_spike', 'critical', 
        'High Error Rate Detected',
        `Error rate has reached ${(metrics.errorRate * 100).toFixed(2)}%`,
        { errorRate: metrics.errorRate }
      );
    }
    
    // Performance degradation alert
    const performanceDrop = previousMetrics.performanceScore - metrics.performanceScore;
    if (performanceDrop > this.alertThresholds.performanceDrop) {
      this.createAlert('performance_degradation', 'warning',
        'Performance Degradation Detected',
        `Performance score dropped by ${performanceDrop.toFixed(1)} points`,
        { performanceScore: metrics.performanceScore, drop: performanceDrop }
      );
    }
    
    // User dropoff alert
    const userDrop = (previousMetrics.activeUsers - metrics.activeUsers) / previousMetrics.activeUsers;
    if (userDrop > this.alertThresholds.userDropoff) {
      this.createAlert('user_drop', 'warning',
        'Active User Decline',
        `Active users dropped by ${(userDrop * 100).toFixed(1)}%`,
        { activeUsers: metrics.activeUsers, drop: userDrop }
      );
    }
    
    // Conversion rate drop alert
    const conversionDrop = (previousMetrics.conversionRate - metrics.conversionRate) / previousMetrics.conversionRate;
    if (conversionDrop > this.alertThresholds.conversionDrop) {
      this.createAlert('conversion_drop', 'warning',
        'Conversion Rate Decline',
        `Conversion rate dropped by ${(conversionDrop * 100).toFixed(1)}%`,
        { conversionRate: metrics.conversionRate, drop: conversionDrop }
      );
    }
  }

  /**
   * Create a monitoring alert
   */
  private createAlert(
    type: MonitoringAlert['type'],
    severity: MonitoringAlert['severity'],
    title: string,
    description: string,
    metrics: Record<string, number>
  ): void {
    const alert: MonitoringAlert = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      severity,
      title,
      description,
      timestamp: new Date().toISOString(),
      resolved: false,
      metrics
    };
    
    this.alerts.push(alert);
    
    // Log alert in development
    if (!isProduction()) {
      console.warn('Monitoring Alert:', alert);
    }
  }

  /**
   * Get stored data from localStorage
   */
  private getStoredData(key: string, defaultValue: any[] = []): any[] {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  /**
   * Start periodic monitoring
   */
  private startPeriodicMonitoring(): void {
    // Collect metrics every 5 minutes
    setInterval(async () => {
      try {
        await this.getCurrentMetrics();
      } catch (error) {
        console.warn('Failed to collect monitoring metrics:', error);
      }
    }, 5 * 60 * 1000);
  }

  /**
   * Setup alert system
   */
  private setupAlertSystem(): void {
    // In a real implementation, this would integrate with alerting services
    // For now, we'll just log alerts to console
    console.log('Alert system initialized');
  }

  /**
   * Load historical data
   */
  private loadHistoricalData(): void {
    try {
      const stored = localStorage.getItem('postLaunchMonitoring_history');
      if (stored) {
        this.metricsHistory = JSON.parse(stored);
      }
      
      const storedAlerts = localStorage.getItem('postLaunchMonitoring_alerts');
      if (storedAlerts) {
        this.alerts = JSON.parse(storedAlerts);
      }
    } catch (error) {
      console.warn('Failed to load historical monitoring data:', error);
    }
  }


}

// Export singleton instance
export const postLaunchMonitoringService = new PostLaunchMonitoringService();